import { useEffect, useContext, useState } from 'react';
import { Box } from '@mui/material';
import {
	EditOutlined,
	DeleteOutlineOutlined,
	ControlPointDuplicateOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import MaterialTable from '@material-table/core';
import { UserContext } from '../../contexts/UserContext';
import { generateClient } from 'aws-amplify/api';
import { deleteSegment, createAuditLog } from '../../graphql/mutations';
import { segmentByTenant } from '../../graphql/queries';
import { PageAppBar } from 'src/components/pageAppBar';
import { ConfirmDialog } from 'src/components/confirmDialog/confirmDialog';
import { InUseDialog } from 'src/components/inUseDialog/inUseDialog';
import { checkSegmentUsage } from 'src/tools/checkUsage';
import { fetchAllPages } from 'src/tools/graphqlPaging';

export function Segments() {
	const client = generateClient();
	// const classes = useStyles();
	const userContext = useContext(UserContext);
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const [segments, setSegments] = useState([]);
	const [toDelete, setToDelete] = useState(null);
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
	const [inUseOpen, setInUseOpen] = useState(false);
	const [inUseInfo, setInUseInfo] = useState(null);

	useEffect(() => {
		async function getData() {
			setLoading(true);
			const allSegments = await fetchAllPages({
				client,
				query: segmentByTenant,
				resultKey: 'segmentByTenant',
				variables: { tenant: userContext.tenantId },
			});
			setSegments(
				allSegments.sort(
					(a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
				),
			);
			setLoading(false);
		}
		if (userContext.tenantId) {
			getData();
		}
	}, [userContext.tenantId]);

	async function handleDelete() {
		setLoading(true);

		// Check if segment is in use before deleting. This blocks deletion for
		// segments referenced by ANY campaign (Active, Paused, Pending, etc.),
		// including clones of completed campaigns which start in a Pending state.
		const isInUse = await checkSegmentUsage(toDelete, userContext.tenantId);
		if (isInUse.inUse) {
			setInUseInfo({
				message: isInUse.message,
				usedBy: isInUse.usedBy || [],
			});
			setInUseOpen(true);
			setLoading(false);
			setToDelete(null);
			setConfirmDeleteOpen(false);
			return;
		}

		const deleteResult = await client.graphql({
			query: deleteSegment,
			variables: { input: { id: toDelete } },
		});
		console.log(deleteResult);
		await client.graphql({
			query: createAuditLog,
			variables: {
				input: {
					tenant: userContext.tenantId,
					type: 'Segment',
					timestamp: new Date().toISOString(),
					userName: userContext.email,
					documentId: toDelete,
					name: segments.find((segment) => segment.id === toDelete).name,
					currentValue: '{}',
					action: 'DELETED',
					ttl: Math.floor(Date.now() / 1000) + 6 * 30 * 24 * 60 * 60, //6 months
				},
			},
		});
		//if segment is successful in deleting remove it from the current list.
		if (deleteResult.data) {
			const deletedSegment = deleteResult.data.deleteSegment;
			const curSegments = [...segments];
			curSegments.splice(
				curSegments.findIndex((e) => e.id === deletedSegment.id),
				1,
			);
			setSegments([...curSegments]);
		}

		setLoading(false);
		setToDelete(null);
		setConfirmDeleteOpen(false);
	}

	function handleCancelDelete() {
		setToDelete(null);
		setConfirmDeleteOpen(false);
	}

	function handleCloseInUse() {
		setInUseOpen(false);
		setInUseInfo(null);
	}

	function handleSearchChange(value) {
		window.sessionStorage.setItem('SegmentsSearch', value);
	}

	return (
		<Box>
			<PageAppBar
				title="Segments"
				description="Build segments to target specific groups of contacts."
				actionOneText={
					userContext.securityOptions.inventoryManagement > 1 ? '+ Segment' : ''
				}
				actionOneHandler={() => {
					navigate('/segments/new');
				}}
				actionOneDisabled={userContext.securityOptions.inventoryManagement < 2}
			/>
			<MaterialTable
				components={{
					Container: (props) => <Box {...props} elevation={0} />,
				}}
				title=""
				data={segments}
				columns={[
					{ title: 'Name', field: 'name' },
					{ title: 'Description', field: 'description' },
					{ title: 'Suppression Profile', field: 'suppression.name' },
				]}
				isLoading={loading}
				options={{
					actionsColumnIndex: -1,
					pageSize: 10,
					searchText: window.sessionStorage.getItem('SegmentsSearch'),
					searchFieldStyle: {
						marginBottom: '16px',
						marginleft: '-28px',
					},
					headerStyle: {
						borderTop: '1px solid #e0e0e0',
						padding: '16px',
					},
					searchFieldVariant: 'outlined',
					paginationType: 'stepped',
				}}
				onSearchChange={handleSearchChange}
				actions={[
					{
						disabled: userContext.securityOptions.inventoryManagement < 2,
						icon: () => (
							<EditOutlined
								color={
									userContext.securityOptions.inventoryManagement < 2
										? 'disabled'
										: 'primary'
								}
							/>
						),
						tooltip: 'Edit Segment',
						onClick: (event, rowData) => {
							navigate(`/segments/${rowData.id}?op=edit`);
						},
					},
					(rowData) =>
						(function () {
							const disabled =
								userContext.securityOptions.inventoryManagement < 2;
							return {
								icon: () => (
									<ControlPointDuplicateOutlined
										color={disabled ? 'disabled' : 'primary'}
									/>
								),
								tooltip: 'Clone Segment',
								onClick: (event, rowData) => {
									navigate(`/segments/${rowData.id}?op=clone`);
								},
								disabled,
							};
						})(),
					(rowData) =>
						(function () {
							const disabled =
								userContext.securityOptions.inventoryManagement < 2;
							return {
								icon: () => (
									<DeleteOutlineOutlined
										color={disabled ? 'disabled' : 'primary'}
									/>
								),
								tooltip: 'Delete Segment',
								onClick: (event, rowData) => {
									setToDelete(rowData.id);
									setConfirmDeleteOpen(true);
								},
								disabled,
							};
						})(),
				]}
				onRowClick={(event, rowData) => {
					navigate(`/segments/${rowData.id}?op=edit`);
				}}
			/>
			<ConfirmDialog
				open={confirmDeleteOpen}
				title="Delete Segment"
				description="Are you sure you want to delete this segment?"
				actionOneText="Delete"
				actionOneHandler={() => {
					handleDelete();
				}}
				actionTwoText="Cancel"
				actionTwoHandler={() => {
					handleCancelDelete();
				}}
			/>
			<InUseDialog
				open={inUseOpen}
				title="Segment In Use"
				message={inUseInfo?.message}
				usedBy={inUseInfo?.usedBy || []}
				onClose={handleCloseInUse}
			/>
		</Box>
	);
}
